import Vue from "vue";
import { ActionTree, MutationTree, GetterTree } from "vuex";

const initialState: GroupState = {
  all: [],
  igmp_querier_ips: undefined,
  boundary_clock: undefined,
  boundary_profiles: [],
};

const getters: GetterTree<GroupState, RootState> = {
  all(state: GroupState): Group[] {
    return state.all;
  },
  igmpQuerierIps(state: GroupState): QuerierIps | undefined {
    return state.igmp_querier_ips;
  },
  boundaryClock(state: GroupState): ClockSettings | undefined {
    return state.boundary_clock;
  },
  boundaryProfiles(state: GroupState): BoundaryProfile[] {
    return state.boundary_profiles;
  },
};

const mutations: MutationTree<GroupState> = {
  RESET(state: GroupState) {
    state.all = [];
    state.boundary_clock = null;
    state.boundary_profiles = [];
  },

  SET_MODELS(state: GroupState, payload: Group[]) {
    state.all = payload.sort((a, b) => (a.group_id > b.group_id ? 1 : b.group_id > a.group_id ? -1 : 0));
  },

  UPDATE_MODEL(state: GroupState, payload: Group) {
    const index = state.all.findIndex((model: Group) => model.group_id === payload.group_id);

    if (index === -1) {
      state.all.push(payload);
    } else {
      Vue.set(state.all, index, payload);
    }
  },

  REMOVE_MODELS(state: GroupState, payload: number) {
    state.all = state.all.filter((group: Group) => group.group_id !== payload);
  },

  UPDATE_QUERIER_IPS(state: GroupState, payload: QuerierIps) {
    state.igmp_querier_ips = payload;
  },

  UPDATE_BOUNDARY_CLOCK(state: GroupState, payload: ClockSettings) {
    state.boundary_clock = payload;
  },

  UPDATE_BOUNDARY_PROFILES(state: GroupState, payload: BoundaryProfile[]) {
    state.boundary_profiles = payload;
  },
};

const actions: ActionTree<GroupState, RootState> = {
  reset({ commit }) {
    commit("RESET");
  },

  index({ commit, rootState, state }, stash?: boolean) {
    if (state.all.length > 0 && !stash) {
      return new Promise((resolve) => resolve(this.getters["group/all"]));
    }

    return rootState.api
      .get(`groups/group`)
      .then((response: { data: Group[] }) => {
        commit("SET_MODELS", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  read({ commit, rootState, state }, payload: ReadGroupPayload) {
    return rootState.api
      .get(`groups/group/${payload.group_id}`)
      .then((response: { data: Group }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  create({ commit, rootState }, payload: CreateGroupPayload) {
    return rootState.api
      .post(`groups/group`, payload)
      .then((response: { data: Group }) => {
        if (response.data) {
          commit("SET_MODELS", response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  move({ commit, rootState }, payload: MoveGroupPayload) {
    return rootState.api
      .put(`groups/move`, { from: payload.source_id, to: payload.target_id })
      .then((response: { data: Group }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  update({ commit, rootState }, payload: UpdateGroupPayload) {
    return rootState.api
      .put(`groups/group/${payload.group_id}`, payload)
      .then((response: { data: Group }) => {
        if (response.data) {
          commit("UPDATE_MODEL", response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  delete({ commit, rootState, state }, payload: number) {
    return rootState.api
      .delete(`groups/group/${payload}`)
      .then((response: { data: {} }) => {
        commit("REMOVE_MODELS", payload);
        return Promise.resolve(payload);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  bulkUpdate({ commit, rootState }, payload: Group[]) {
    return rootState.api
      .put(`groups/group`, payload)
      .then((response: { data: Group }) => {
        commit("SET_MODELS", payload);

        return Promise.resolve(payload);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readIgmpQuerierIps({ commit, rootState, state }) {
    if (state.igmp_querier_ips) {
      return new Promise((resolve) => resolve(this.getters["group/igmpQuerierIps"]));
    }

    return rootState.api
      .get(`snooping/querier_ips`)
      .then((response: { data: QuerierIps }) => {
        commit("UPDATE_QUERIER_IPS", response.data);
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readBoundaryClock({ commit, rootState, state }, groupId: number) {
    return rootState.api
      .get(`groups/group/${groupId}/boundary_clock`)
      .then((response: { data: ClockSettings }) => {
        commit("UPDATE_BOUNDARY_CLOCK", response.data);
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },
  updateBoundaryClock({ commit, rootState, state }, payload: any) {
    return rootState.api
      .put(`groups/group/${payload.group_id}/boundary_clock`, payload)
      .then((response: { data: ClockSettings }) => {
        commit("UPDATE_BOUNDARY_CLOCK", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },
  readBoundaryProfiles({ commit, rootState, state }) {

    return rootState.api
      .get(`ptp/boundary/profiles`)
      .then((response: { data: BoundaryProfile[] }) => {
        commit("UPDATE_BOUNDARY_PROFILES", response.data);
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
